// Email validation regex (RFC 5322 compliant)
export const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/**
 * Validates an email address format
 */
export const validateEmail = (email: string): string | null => {
    if (!email.trim()) {
        return "Email is required";
    }
    if (!EMAIL_REGEX.test(email)) {
        return "Please enter a valid email";
    }
    return null;
};

/**
 * Validates password complexity
 */
export const validatePassword = (pwd: string, firstName?: string, lastName?: string): string | null => {
    if (pwd.length < 8) {
        return "Password must be at least 8 characters";
    }

    // Check for parts of name if provided
    if (firstName || lastName) {
        const nameParts = [firstName?.toLowerCase(), lastName?.toLowerCase()].filter(Boolean) as string[];
        const pwdLower = pwd.toLowerCase();
        for (const part of nameParts) {
            if (part && pwdLower.includes(part)) {
                return "Password cannot contain your name";
            }
        }
    }

    // Check weak passwords
    const weakPasswords = ['password123', 'ace12345', 'password', '12345678'];
    if (weakPasswords.includes(pwd.toLowerCase())) {
        return "This password is too weak or commonly used";
    }

    // Check character complexity (3 of 4 categories)
    const hasUppercase = /[A-Z]/.test(pwd);
    const hasLowercase = /[a-z]/.test(pwd);
    const hasDigit = /\d/.test(pwd);
    const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd);

    const categoriesCount = [hasUppercase, hasLowercase, hasDigit, hasSpecial].filter(Boolean).length;

    if (categoriesCount < 3) {
        return "Password must contain at least 3 of: uppercase, lowercase, numbers, special characters";
    }

    return null;
};

/**
 * Validates phone number (when text notifications are selected)
 */
export const validatePhoneNumber = (phone: string, isRequired: boolean = true): string | null => {
    if (!isRequired) {
        return null;
    }

    if (!phone.trim()) {
        return "Phone number is required";
    }

    if (phone.replaceAll(/\D/g, "").length < 10) {
        return "Please enter a complete 10-digit phone number";
    }

    return null;
};

/**
 * Validates confirm password matches original
 */
export const validateConfirmPassword = (password: string, confirmPassword: string): string | null => {
    if (!confirmPassword.trim()) {
        return "Please re-enter your password";
    }

    if (password !== confirmPassword) {
        return "Passwords do not match";
    }

    return null;
};

/**
 * Validates Workday ID format
 */
export const validateWorkdayId = (workdayId: string): string | null => {
    if (!workdayId.trim()) {
        return "Workday ID is required";
    }
    if (!/^\d{3,9}$/.test(workdayId.trim())) {
        return "Workday ID must at least be 3 numeric characters";
    }
    return null;
};

/**
 * Formats phone number as user types
 */
export const formatPhoneNumber = (value: string): string => {
    const numericValue = value.replaceAll(/\D/g, "").slice(0, 10);
    if (numericValue.length > 6) {
        return `${numericValue.slice(0, 3)}-${numericValue.slice(3, 6)}-${numericValue.slice(6)}`;
    } else if (numericValue.length > 3) {
        return `${numericValue.slice(0, 3)}-${numericValue.slice(3)}`;
    }
    return numericValue;
};

// ================================================================
// FULL NAME (single-field profile "Name")
// ================================================================

/** Backend column is NVARCHAR(255); leave headroom. */
export const NAME_MAX_LENGTH = 240;

/** Letters (incl. common accented), spaces, hyphens and apostrophes. */
const NAME_ALLOWED_REGEX = /^[\p{L}\s'-]+$/u;

/**
 * Collapses internal whitespace runs to a single space and trims ends.
 * Use this before validating or before submitting to the backend.
 */
export const normalizeFullName = (value: string): string =>
    value.replace(/\s+/g, ' ').trim();

/**
 * Validates a single "Full Name" input.
 * Returns an error message or null if valid.
 */
export const validateFullName = (value: string): string | null => {
    const normalized = normalizeFullName(value ?? '');
    if (!normalized) {
        return "Name is required";
    }
    if (normalized.length > NAME_MAX_LENGTH) {
        return `Name must be ${NAME_MAX_LENGTH} characters or fewer`;
    }
    if (!NAME_ALLOWED_REGEX.test(normalized)) {
        return "Name may contain only letters, spaces, hyphens, and apostrophes";
    }
    return null;
};

/**
 * Splits a single "Full Name" into firstName / lastName on the FIRST space.
 * - "John Van Dyke" -> { firstName: "John", lastName: "Van Dyke" }
 * - "Cher"          -> { firstName: "Cher", lastName: "" }
 */
export const splitFullName = (value: string): { firstName: string; lastName: string } => {
    const normalized = normalizeFullName(value ?? '');
    const firstSpace = normalized.indexOf(' ');
    if (firstSpace === -1) {
        return { firstName: normalized, lastName: '' };
    }
    return {
        firstName: normalized.slice(0, firstSpace).trim(),
        lastName: normalized.slice(firstSpace + 1).trim(),
    };
};

/**
 * Joins firstName + lastName into a single displayable full name.
 */
export const joinFullName = (firstName?: string | null, lastName?: string | null): string =>
    `${firstName ?? ''} ${lastName ?? ''}`.replace(/\s+/g, ' ').trim();